import { compiledClientConfig } from "config";
import { isCSRFError } from "hooks/useNormalizeError";
import { Map } from "immutable";
import { getCsrfToken } from "utils/csrf";
import { devLog } from "utils/devtools/devLogging";
import SessionExpiredError from "utils/errors/sessionExpiredError";

import ApiError from "../errors/apiError";
import { logrocketGetSessionUrl, logrocketLog, removeUrlParams } from "../logrocket";

export type TBody = null | object | string;
export type TMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

export type TOptions = {
  abortSignal?: AbortSignal | null;
  expectJson?: boolean;
  expectJsonOnError?: boolean;
  multipart?: boolean;
  noCache?: boolean;
  pathPrefix?: string;
  redirect?: RequestRedirect;
  status404IsNull?: boolean;
  suppressError?: boolean;
  validateStatusCodes?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TResult<T = any> = { data: T; response: Response };

type HTTPError = { code?: number };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let ongoingRequests = Map<string, Promise<{ data: any; response: Response }>>();

const createHash = (value: string) => {
  let hash = 0;
  let chr: number;
  for (let i = 0; i < value.length; i++) {
    chr = value.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const getRequestKey = (method: TMethod, path: string, body: TBody = null) => {
  let stringBody = "";
  if (body) {
    stringBody = typeof body === "string" ? body : JSON.stringify(body);
  }
  const bodyHash = createHash(stringBody);
  return `${method} ${path} ${bodyHash}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function apiReq<T = any>(
  method: TMethod,
  path: string,
  body: TBody = null,
  options: TOptions = {},
): Promise<TResult<T>> {
  const requestKey = getRequestKey(method, path, body);

  if (ongoingRequests.has(requestKey) && !options.noCache) {
    return ongoingRequests.get(requestKey)!;
  }
  try {
    const promise = executeRequest(method, path, body, options);
    ongoingRequests = ongoingRequests.set(requestKey, promise);
    const response = await promise;
    ongoingRequests = ongoingRequests.delete(requestKey);
    return response;
  } catch (error) {
    ongoingRequests = ongoingRequests.delete(requestKey);
    throw error;
  }
}

const LONG_REQUEST_TIME = 10000; // 10 seconds

const logLongRequest = (method: TMethod, path: string) => {
  logrocketLog("Long api request", { location: "apiReq", method, path: removeUrlParams(path) });
};

const executeRequest = async (
  method: TMethod,
  path: string,
  body: TBody = null,
  {
    abortSignal = null,
    expectJson = true,
    expectJsonOnError = true,
    multipart = false,
    pathPrefix = compiledClientConfig.spog ? `/${compiledClientConfig.basePrefix}/api` : "/api",
    redirect = undefined,
    status404IsNull = true,
    suppressError = false,
    validateStatusCodes = true,
  }: TOptions = {},
): Promise<TResult> => {
  const headers = {} as { [key: string]: string };
  const options = { headers, method } as RequestInit;

  if (redirect) {
    options.redirect = redirect;
  }

  if (abortSignal) options.signal = abortSignal;

  if (typeof body === "string" || (multipart && body instanceof FormData)) {
    options.body = body;
  } else if (body instanceof Object) {
    options.body = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }

  const logrocketSessionUrl = logrocketGetSessionUrl();
  if (logrocketSessionUrl) {
    headers["logrocket-session-url"] = logrocketSessionUrl;
  }

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  let response: null | Response = null;
  let data = null;
  const url = pathPrefix + path;
  const longRequestTimeout = setTimeout(() => {
    logLongRequest(method, url);
  }, LONG_REQUEST_TIME);
  try {
    response = await fetch(url, options);
  } catch (error) {
    if (!(error instanceof Error)) throw new Error();
    if ((error as HTTPError)?.code === DOMException.ABORT_ERR) throw new Error("Request aborted");

    const stringifiedError = JSON.stringify(error, Object.getOwnPropertyNames(error));
    logrocketLog(`Failed to fetch ${method} ${removeUrlParams(url)}.`, {
      error: stringifiedError,
    });
    devLog({ extra: { error: stringifiedError, method, url }, level: "error", message: "Failed to fetch" });
    throw ApiError.from(error, { suppressError });
  } finally {
    clearTimeout(longRequestTimeout);
  }

  const thereWasAnError = !response.ok;
  if (expectJson || (expectJsonOnError && thereWasAnError)) {
    try {
      data = await response.json();
    } catch (error) {
      if ((error as HTTPError)?.code === DOMException.ABORT_ERR) throw new Error("Request aborted");

      throw new ApiError({
        errorId: response.status === 504 ? "fetch.timeOut" : "fetch.receivedNonJsonResponse",
        statusCode: response.status,
        suppressError,
      });
    }
  }

  if (!validateStatusCodes || (response.status >= 200 && response.status < 300)) return { data, response };
  else if (response.status === 404 && status404IsNull) {
    throw new ApiError({
      errorId: "resource.notFound",
      suppressError,
    });
  } else if (response.status === 401) {
    // unauthorized
    try {
      await fetch(`${pathPrefix}/v1/signOut`, { method: "POST" });
    } catch (_error) {
      console.log("Failed to sign out after getting Unauthorized, the auth token is probably expired");
    }

    throw new SessionExpiredError();
  } else if (response.status === 403) {
    // forbidden
    if (isCSRFError(data, response.status)) {
      throw ApiError.from(data, {
        errorId: "fetch.forbiddenRequest",
        statusCode: response.status,
        suppressError,
      });
    } else {
      throw new ApiError({
        errorId: "fetch.forbiddenRequest",
        statusCode: 403,
        suppressError,
      });
    }
  } else if (response.status >= 400) {
    throw ApiError.from(data, {
      statusCode: response.status,
      suppressError,
    });
  } else {
    throw new ApiError({
      errorId: "fetch.unexpectedStatusCode",
      params: {
        statusCode: response.status,
      },
      statusCode: response.status,
      suppressError,
    });
  }
};

export const toQuerystring = (queryData: Record<string, unknown>): string => {
  const query = new URLSearchParams();
  Object.entries(queryData).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      query.append(key, value.join(","));
    } else if (typeof value === "object") {
      query.append(key, JSON.stringify(value));
    } else {
      query.append(key, String(value as PropertyKey));
    }
  });
  return query.toString();
};
